/**
 * Brand palette — the second runtime axis of docs/010-theming-and-tokens.md ("Palettes").
 *
 * Orthogonal to the light/dark mode in `./theme-mode.ts`: the mode decides WHICH block of a
 * theme applies, the palette decides which brand ramp those blocks use. The colours live
 * in `src/styles/themes/palettes.css`; nothing here knows one. This module only sets (or
 * clears) `data-palette` on `<html>`:
 *
 *   "instacheckin" (default)  →  attribute removed  → themes/default.css's brand stands
 *   any other id              →  data-palette="<id>" → that palette's override blocks apply
 *
 * React-free on purpose, like theme-mode.ts: the pre-paint inline script in each app's
 * `index.html` does the same thing before any bundle loads. If you change
 * `PALETTE_STORAGE_KEY`, the attribute, or the id list, change those scripts too.
 */
/** Every selectable palette, in the order a picker presents them. Default first. */
export const PALETTE_IDS = [
    "instacheckin",
    "indigo",
    "indigo-nav",
    "emerald",
    "teal",
    "slate",
    "sunset",
];
/** The rebrand default: the logo's sky blue. Its values ARE themes/default.css. */
export const DEFAULT_PALETTE = "instacheckin";
/**
 * Palettes whose sidebar is a dark surface in BOTH modes (themes/palettes.css re-points the
 * nav subtree's text tokens). The wordmark must use its near-white variant there even in
 * light mode — an asset swap, the one use of this list.
 */
export const DARK_NAV_PALETTES = ["indigo-nav"];
/** Shared with the pre-paint script in `index.html`. Namespaced like `instanexus.theme`. */
export const PALETTE_STORAGE_KEY = "instanexus.palette";
const PALETTE_ATTRIBUTE = "data-palette";
export function isPaletteId(value) {
    return (typeof value === "string" &&
        PALETTE_IDS.includes(value));
}
/**
 * The stored palette, or the default. An unreadable store (Safari private mode) or a value
 * a newer build wrote and this one does not know degrades to the default — never a throw.
 */
export function readStoredPalette() {
    if (typeof localStorage === "undefined")
        return DEFAULT_PALETTE;
    try {
        const stored = localStorage.getItem(PALETTE_STORAGE_KEY);
        return isPaletteId(stored) ? stored : DEFAULT_PALETTE;
    }
    catch {
        return DEFAULT_PALETTE;
    }
}
/** Persists the palette. Silently a no-op when storage is unavailable. */
export function writeStoredPalette(palette) {
    if (typeof localStorage === "undefined")
        return;
    try {
        localStorage.setItem(PALETTE_STORAGE_KEY, palette);
    }
    catch {
        /* Storage unavailable — the palette lives for this session only. */
    }
}
/**
 * Writes the palette to `<html>`. The default REMOVES the attribute rather than writing
 * `data-palette="instacheckin"`: default.css already is that palette, and an absent
 * attribute is what a first-time visitor (and the pre-paint script with nothing stored)
 * produces, so both paths land on the same DOM.
 */
export function applyPalette(palette) {
    if (typeof document === "undefined")
        return;
    const root = document.documentElement;
    if (palette === DEFAULT_PALETTE) {
        root.removeAttribute(PALETTE_ATTRIBUTE);
    }
    else {
        root.setAttribute(PALETTE_ATTRIBUTE, palette);
    }
}
//# sourceMappingURL=palette.js.map